import { defineStore } from 'pinia'
import { ref } from 'vue'

import { enhanceWithFloat } from '../utils/checkContentBlockFloat'
import { injectAdSlotMarkers } from '../utils/injectAdSlotMarkers'
import { splitTextBlocks } from '../utils/split-text-blocks'
import { injectRelatedArticlesBlock } from '../utils/injectRelatedArticlesBlock'

import type { AdPlacement } from '../../advertisement/types/ad-slot-types'
import type { Dimensions } from '../../composables/use-measure'
import type { ContentBlocksAndAdSlotMarkers, DeviceData } from '../types/content-block-types'

export interface NewContentBlock<T extends ContentBlocksAndAdSlotMarkers> {
  id: string,
  type: string,
  data: T,
  dimensions: Dimensions,
  enabled: boolean,
  shouldFloat?: boolean
}

export const useContentBlockStore = defineStore('content-blocks', () => {
  const contentBlockList = ref<NewContentBlock<ContentBlocksAndAdSlotMarkers>[]>([])

  /**
   * Update a content block's dimensions
   * @param id - Unique identifier for the content block
   * @param dimensions - The dimensions of the content block
   */
  function updateContentBlockDimensions (id: string, dimensions: Dimensions) {
    // Check if the block already exists
    const existingBlockIndex = contentBlockList.value.findIndex(block => block.id === id)

    if (existingBlockIndex >= 0 && contentBlockList.value[existingBlockIndex]) {
      // Create a new array with the updated item
      contentBlockList.value[existingBlockIndex].dimensions = dimensions
    }
  }

  /**
   * Get dimensions for a specific content block
   * @param id - Unique identifier for the content block
   * @returns The dimensions of the content block or undefined if not found
   */
  function getContentBlockDimensions (id: string): Dimensions | undefined {
    const block = contentBlockList.value.find(block => block.id === id)
    return block?.dimensions
  }

  /**
   * Remove a content block's dimensions from the store
   * @param id - Unique identifier for the content block
   */
  function removeContentBlockDimensions (id: string) {
    contentBlockList.value = contentBlockList.value.filter(block => block.id !== id)
  }

  /**
   * Clear all content block dimensions
   */
  function clearAllContentBlockDimensions () {
    contentBlockList.value = []
  }

  function enableContentBlock (id: string) {
    const block = contentBlockList.value.find(block => block.id === id)
    if (block) {
      block.enabled = true
    }
  }

  function getContentBlockEnabled (id: string): boolean | undefined {
    const block = contentBlockList.value.find(block => block.id === id)
    return block?.enabled
  }

  function setContentBlockList <T extends ContentBlocksAndAdSlotMarkers> (list: NewContentBlock<T>[], adPlacement?: AdPlacement | null, deviceData?: DeviceData) {
    // 1st step is to split text blocks
    // TODO: Remove this step when fixed in CMS @see https://bauer-specialist.atlassian.net/browse/TCDW-483
    const contentBlocksWithSplitText = splitTextBlocks(list)
    // 2nd step is to inject ad slot markers
    const contentBlocksWithAdSlotMarkers = injectAdSlotMarkers(contentBlocksWithSplitText, adPlacement, deviceData)
    // 3rd step is to enhance the content blocks with float information
    const contentBlocksEnhancedWithFloat = enhanceWithFloat(contentBlocksWithAdSlotMarkers, deviceData)
    // 4th step is to inject related articles blocks
    const contentBlocksWithRelatedArticles = injectRelatedArticlesBlock(contentBlocksEnhancedWithFloat, adPlacement, deviceData)

    contentBlockList.value = contentBlocksWithRelatedArticles
  }

  // TODO: verify using the type
  function getContentBlockById <T extends ContentBlocksAndAdSlotMarkers['paragraphType']> (id: string, type: T): NewContentBlock<Extract<ContentBlocksAndAdSlotMarkers, { paragraphType: T }>> {
    const block = contentBlockList.value.find(block => block.id === id)

    if (!block) {
      throw new Error(`Content block type ${type} with id ${id} not found`)
    }

    return block as NewContentBlock<Extract<ContentBlocksAndAdSlotMarkers, { paragraphType: T }>>
  }

  return {
    // Content block list
    contentBlockList,
    setContentBlockList,

    // Content block by id
    getContentBlockById,

    // Content block dimensions
    updateContentBlockDimensions,
    getContentBlockDimensions,
    removeContentBlockDimensions,
    clearAllContentBlockDimensions,

    // TODO
    enableContentBlock,
    getContentBlockEnabled
  }
})
